import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, ChevronRight, ChevronDown, Monitor, Smartphone, Palette, PenTool, TrendingUp, ShoppingCart, Store, LayoutTemplate, Code2, Database, ShieldCheck, Cloud, HeartPulse, GraduationCap, Building2, Truck, Plane, Home, Factory, Coffee, Cpu, Car, Zap, Type, Link as LinkIcon, Languages, FileImage, Sparkles, Eraser, Keyboard, Lock, Globe } from 'lucide-react';
import { useState, useEffect } from 'react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedMobileSection, setExpandedMobileSection] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
    setExpandedMobileSection(null);
  }, [location.pathname]);

  const toggleMobileSection = (section: string) => {
    setExpandedMobileSection(expandedMobileSection === section ? null : section);
  };

  const servicesMenu = {
    core: [
      { name: 'Web Development', desc: 'Custom websites & web applications', icon: <Monitor className="w-5 h-5 text-blue-600" />, path: '/services/web-development', bgColor: 'bg-blue-100' },
      { name: 'Mobile Apps', desc: 'iOS & Android applications', icon: <Smartphone className="w-5 h-5 text-emerald-600" />, path: '/services/mobile-app', bgColor: 'bg-emerald-100' },
    ],
    design: [
      { name: 'UI/UX Design', desc: 'User experience & interface design', icon: <Palette className="w-5 h-5 text-pink-600" />, path: '/services/uiux-design', bgColor: 'bg-pink-100' },
      { name: 'Logo & Brand', desc: 'Brand identity & logo design', icon: <PenTool className="w-5 h-5 text-amber-600" />, path: '/services/logo-brand-design', bgColor: 'bg-amber-100' },
      { name: 'Digital Marketing', desc: 'SEO, PPC & social media', icon: <TrendingUp className="w-5 h-5 text-teal-600" />, path: '/services/digital-marketing', bgColor: 'bg-teal-100' },
    ],
    ecommerce: [
      { name: 'E-commerce', desc: 'Online stores & marketplaces', icon: <ShoppingCart className="w-5 h-5 text-yellow-600" />, path: '/services/ecommerce-development', bgColor: 'bg-yellow-100' },
      { name: 'Shopify', desc: 'Shopify store development', icon: <Store className="w-5 h-5 text-green-600" />, path: '/services/shopify-development', bgColor: 'bg-green-100' },
      { name: 'WordPress', desc: 'CMS & blog development', icon: <LayoutTemplate className="w-5 h-5 text-indigo-600" />, path: '/services/wordpress-development', bgColor: 'bg-indigo-100' },
    ],
    bottom: [
      { name: 'React', icon: <Code2 className="w-5 h-5 text-cyan-500" />, path: '/services/react-development' },
      { name: 'Laravel', icon: <Database className="w-5 h-5 text-red-500" />, path: '/services/laravel-development' },
      { name: 'QA Testing', icon: <ShieldCheck className="w-5 h-5 text-blue-500" />, path: '/services/testing-services' },
      { name: 'Cloud DevOps', icon: <Cloud className="w-5 h-5 text-sky-500" />, path: '/services/cloud-devops' },
    ]
  };

  const industriesMenu = [
    { name: 'Healthcare', desc: 'Medical & Health Tech', icon: <HeartPulse className="w-5 h-5 text-rose-500" />, path: '/industries/healthcare-medical', bgColor: 'bg-rose-100' },
    { name: 'Education', desc: 'E-learning & EdTech', icon: <GraduationCap className="w-5 h-5 text-blue-500" />, path: '/industries/education-elearning', bgColor: 'bg-blue-100' },
    { name: 'Finance', desc: 'Banking & FinTech', icon: <Building2 className="w-5 h-5 text-emerald-500" />, path: '/industries/finance-banking', bgColor: 'bg-emerald-100' },
    { name: 'E-commerce', desc: 'Retail & Online Stores', icon: <ShoppingCart className="w-5 h-5 text-amber-500" />, path: '/industries/ecommerce-retail', bgColor: 'bg-amber-100' },
    { name: 'Logistics', desc: 'Supply Chain & Transport', icon: <Truck className="w-5 h-5 text-cyan-500" />, path: '/industries/logistics-supply-chain', bgColor: 'bg-cyan-100' },
    { name: 'Travel', desc: 'Tourism & Hospitality', icon: <Plane className="w-5 h-5 text-teal-500" />, path: '/industries/travel-hospitality', bgColor: 'bg-teal-100' },
    { name: 'Real Estate', desc: 'Property Management', icon: <Home className="w-5 h-5 text-purple-500" />, path: '/industries/real-estate-property', bgColor: 'bg-purple-100' },
    { name: 'Manufacturing', desc: 'Industrial Solutions', icon: <Factory className="w-5 h-5 text-slate-500" />, path: '/industries/manufacturing-industrial', bgColor: 'bg-slate-200' },
    { name: 'Food & Beverage', desc: 'Restaurant & F&B Tech', icon: <Coffee className="w-5 h-5 text-orange-500" />, path: '/industries/food-beverage', bgColor: 'bg-orange-100' },
    { name: 'Technology', desc: 'Software & SaaS', icon: <Cpu className="w-5 h-5 text-indigo-500" />, path: '/industries/technology-software', bgColor: 'bg-indigo-100' },
    { name: 'Automotive', desc: 'Transportation & Mobility', icon: <Car className="w-5 h-5 text-slate-600" />, path: '/industries/automotive-transportation', bgColor: 'bg-slate-200' },
    { name: 'Energy', desc: 'Utilities & Power', icon: <Zap className="w-5 h-5 text-yellow-500" />, path: '/industries/energy-utilities', bgColor: 'bg-yellow-100' },
  ];

  const toolsMenu = {
    developer: [
      { name: 'JSON Formatter', desc: 'Beautify & validate JSON', icon: <Code2 className="w-5 h-5 text-brand-400" />, path: '/tools/json-formatter' },
      { name: 'Case Converter', desc: 'Text transformation tools', icon: <Type className="w-5 h-5 text-emerald-400" />, path: '/tools/case-converter' },
      { name: 'Prompt to URL', desc: 'Smart AI link generator', icon: <LinkIcon className="w-5 h-5 text-blue-400" />, path: '/tools/prompt-to-url' },
      { name: 'Bcrypt Generator', desc: 'Secure hash generators', icon: <ShieldCheck className="w-5 h-5 text-purple-400" />, path: '/tools/bcrypt-generator' },
    ],
    image: [
      { name: 'Image Compressor', desc: 'Optimize image file sizes', icon: <FileImage className="w-5 h-5 text-blue-400" />, path: '/tools/image-compressor' },
      { name: 'Image Enhancer', desc: 'Browser-side scaling tools', icon: <Sparkles className="w-5 h-5 text-amber-400" />, path: '/tools/image-enhancer' },
    ],
    utility: [
      { name: 'Typing Test', desc: 'WPM & accuracy metrics', icon: <Keyboard className="w-5 h-5 text-brand-400" />, path: '/tools/typing-test' },
      { name: 'Color Generator', desc: 'Hex & RGB generation', icon: <Palette className="w-5 h-5 text-pink-400" />, path: '/tools/color-generator' },
      { name: 'Password Generator', desc: 'Secure random strings', icon: <Lock className="w-5 h-5 text-blue-400" />, path: '/tools/password-generator' },
      { name: 'What is my IP?', desc: 'Public IP metrics', icon: <Globe className="w-5 h-5 text-emerald-400" />, path: '/tools/what-is-my-ip' },
      { name: 'Live Translate', desc: 'Real-time translation', icon: <Languages className="w-5 h-5 text-brand-400" />, path: '/tools/live-translate' },
    ]
  };

  return (
    <header
      className={`fixed z-50 transition-all duration-700 ${scrolled
          ? 'bg-[#030507]/80 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.8)] py-2'
          : 'bg-white/2 backdrop-blur-xl py-4'
        } ${
        /* Mobile: Full width, top 0 | Desktop: Floating Pill */
        'top-0 left-0 w-full border-b border-white/5 lg:top-4 lg:left-1/2 lg:-translate-x-1/2 lg:w-[calc(100%-2rem)] lg:max-w-[110rem] lg:rounded-full lg:border lg:border-white/10'
        }`}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center z-50">
            <div className="relative transition-transform hover:scale-105">
              <img
                src="https://www.princeitsolution.com/assets/images/white-logo.png?v=1.2"
                alt="Prince IT"
                className="h-8 md:h-10 w-auto"
                referrerPolicy="no-referrer"
                loading="lazy"
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-4 xl:gap-6 absolute left-1/2 -translate-x-1/2">
            {[
              { name: 'Home', path: '/' },
              { name: 'Monetization', path: 'https://quziko.com/#monetization', isExternal: true },
              { name: 'Services', path: '/services', isDropdown: true, dropdownId: 'services' },
              { name: 'Industries', path: '/industries', isDropdown: true, dropdownId: 'industries' },
              { name: 'Tools', path: '/tools', isDropdown: true, dropdownId: 'tools' },
              { name: 'Portfolio', path: '/portfolio' },
              { name: 'Blog', path: '/blog' },
              { name: 'About', path: '/about-us' }
            ].map((item) => {
              const isActive = item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);

              const NavLink = ({ children, className }: any) => item.isExternal ? (
                <a href={item.path} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>
              ) : (
                <Link to={item.isDropdown ? '#' : item.path} className={className}>{children}</Link>
              );

              return (
                <div
                  key={item.name}
                  className="relative px-3 py-2 group"
                  onMouseEnter={() => item.isDropdown && setActiveDropdown(item.dropdownId as string)}
                  onMouseLeave={() => item.isDropdown && setActiveDropdown(null)}
                >
                  <NavLink className={`text-sm font-bold transition-all duration-300 flex items-center gap-1 ${isActive ? 'text-brand-400 scale-105' : 'text-slate-300 hover:text-white'}`}>
                    {item.name}
                    {item.isDropdown && <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === item.dropdownId ? 'rotate-180' : ''}`} />}
                  </NavLink>

                  {/* Active Underline (Sliding) */}
                  {isActive && (
                    <motion.div
                      layoutId="activeUnderline"
                      className="absolute bottom-0 left-3 right-3 h-[3px] bg-gradient-to-r from-brand-400 to-cyan-400 rounded-full shadow-[0_0_12px_rgba(20,184,166,0.6)]"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}

                  {/* Hover Underline (Fade-in) */}
                  {!isActive && (
                    <div className="absolute bottom-0 left-3 right-3 h-[2px] bg-gradient-to-r from-brand-400/40 to-cyan-400/40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-[0_0_8px_rgba(20,184,166,0.2)]" />
                  )}

                  {/* Dropdowns */}
                  {item.isDropdown && item.dropdownId === 'services' && (
                    <AnimatePresence>
                      {activeDropdown === 'services' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full -left-48 pt-6 w-[800px] z-[60]"
                        >
                          <div className="bg-[#0a0a0a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden backdrop-blur-xl">
                            <div className="grid grid-cols-3 p-6 gap-8 text-left">
                              {/* Core Development */}
                              <div>
                                <h3 className="text-sm font-bold text-white mb-4 border-b border-white/10 pb-2">Core Development</h3>
                                <div className="space-y-4">
                                  {servicesMenu.core.map((s) => (
                                    <Link key={s.name} to={s.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10 group-hover/item:border-brand-500/50">
                                        {s.icon}
                                      </div>
                                      <div>
                                        <div className="text-sm font-bold text-slate-200 group-hover/item:text-brand-400 transition-colors">{s.name}</div>
                                        <div className="text-xs text-slate-400">{s.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              {/* Design & Marketing */}
                              <div>
                                <h3 className="text-sm font-bold text-white mb-4 border-b border-white/10 pb-2">Design & Marketing</h3>
                                <div className="space-y-4">
                                  {servicesMenu.design.map((s) => (
                                    <Link key={s.name} to={s.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10 group-hover/item:border-brand-500/50">
                                        {s.icon}
                                      </div>
                                      <div>
                                        <div className="text-sm font-bold text-slate-200 group-hover/item:text-brand-400 transition-colors">{s.name}</div>
                                        <div className="text-xs text-slate-400">{s.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              {/* E-commerce */}
                              <div>
                                <h3 className="text-sm font-bold text-white mb-4 border-b border-white/10 pb-2">E-commerce</h3>
                                <div className="space-y-4">
                                  {servicesMenu.ecommerce.map((s) => (
                                    <Link key={s.name} to={s.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10 group-hover/item:border-brand-500/50">
                                        {s.icon}
                                      </div>
                                      <div>
                                        <div className="text-sm font-bold text-slate-200 group-hover/item:text-brand-400 transition-colors">{s.name}</div>
                                        <div className="text-xs text-slate-400">{s.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                            {/* Bottom Bar */}
                            <div className="bg-white/5 p-4 border-t border-white/10 flex justify-between items-center px-8">
                              {servicesMenu.bottom.map((s) => (
                                <Link key={s.name} to={s.path} className="flex flex-col items-center gap-1 group/bottom transition-all duration-300 hover:-translate-y-1">
                                  <div className="w-10 h-10 bg-[#050505] rounded-full border border-white/10 flex items-center justify-center group-hover/bottom:border-brand-500/50 transition-all">
                                    {s.icon}
                                  </div>
                                  <span className="text-xs font-medium text-slate-400 group-hover/bottom:text-brand-400">{s.name}</span>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {item.isDropdown && item.dropdownId === 'industries' && (
                    <AnimatePresence>
                      {activeDropdown === 'industries' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full -left-64 pt-6 w-[800px] z-[60]"
                        >
                          <div className="bg-[#0a0a0a] rounded-2xl shadow-2xl border border-white/10 p-6 backdrop-blur-xl">
                            <div className="grid grid-cols-3 gap-4 text-left">
                              {industriesMenu.map((ind) => (
                                <Link key={ind.name} to={ind.path} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-all duration-300 hover:translate-x-1 group/item">
                                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10 group-hover/item:border-brand-500/50">
                                    {ind.icon}
                                  </div>
                                  <div>
                                    <div className="text-sm font-bold text-slate-200 group-hover/item:text-brand-400 transition-colors">{ind.name}</div>
                                    <div className="text-xs text-slate-400">{ind.desc}</div>
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}

                  {item.isDropdown && item.dropdownId === 'tools' && (
                    <AnimatePresence>
                      {activeDropdown === 'tools' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full -left-48 pt-6 w-[800px] z-[60]"
                        >
                          <div className="bg-[#0a0a0a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden backdrop-blur-xl">
                            <div className="grid grid-cols-3 p-6 gap-8 text-left">
                              {/* Developer Tools */}
                              <div>
                                <h3 className="text-sm font-bold text-white mb-4 border-b border-white/10 pb-2">Developer Tools</h3>
                                <div className="space-y-4">
                                  {toolsMenu.developer.map((t) => (
                                    <Link key={t.name} to={t.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10 group-hover/item:border-brand-500/50">
                                        {t.icon}
                                      </div>
                                      <div>
                                        <div className="text-sm font-bold text-slate-200 group-hover/item:text-brand-400 transition-colors">{t.name}</div>
                                        <div className="text-xs text-slate-400">{t.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              {/* Image Tools */}
                              <div>
                                <h3 className="text-sm font-bold text-white mb-4 border-b border-white/10 pb-2">Image Tools</h3>
                                <div className="space-y-4">
                                  {toolsMenu.image.map((t) => (
                                    <Link key={t.name} to={t.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10 group-hover/item:border-brand-500/50">
                                        {t.icon}
                                      </div>
                                      <div>
                                        <div className="text-sm font-bold text-slate-200 group-hover/item:text-brand-400 transition-colors">{t.name}</div>
                                        <div className="text-xs text-slate-400">{t.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                              {/* Utility Tools */}
                              <div>
                                <h3 className="text-sm font-bold text-white mb-4 border-b border-white/10 pb-2">Utility Tools</h3>
                                <div className="space-y-4">
                                  {toolsMenu.utility.map((t) => (
                                    <Link key={t.name} to={t.path} className="flex items-start gap-3 group/item transition-all duration-300 hover:translate-x-1">
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-white/5 border border-white/10 group-hover/item:border-brand-500/50">
                                        {t.icon}
                                      </div>
                                      <div>
                                        <div className="text-sm font-bold text-slate-200 group-hover/item:text-brand-400 transition-colors">{t.name}</div>
                                        <div className="text-xs text-slate-400">{t.desc}</div>
                                      </div>
                                    </Link>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="hidden lg:flex items-center">
            <Link
              to="/contact-us"
              className="relative px-6 py-3 rounded-full text-sm font-bold bg-white text-black transition-all duration-500 hover:bg-brand-400 hover:-translate-y-0.5 flex items-center gap-2 group ml-2"
            >
              <span className="relative z-10">Get a Quote</span>
              <ChevronRight className="relative z-10 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-300 z-50 relative cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden absolute top-full left-0 w-full bg-[#0a0a0a] shadow-2xl border-t border-white/10 overflow-hidden z-50"
          >
            <div className="flex flex-col px-4 py-8 gap-1 max-h-[85vh] overflow-y-auto custom-scrollbar">
              <Link to="/" className="text-lg font-bold p-4 rounded-2xl text-slate-200 hover:bg-white/5 hover:text-brand-400 transition-all">Home</Link>
              <a href="https://quziko.com/#monetization" target="_blank" rel="noopener noreferrer" className="text-lg font-bold p-4 rounded-2xl text-slate-200 hover:bg-white/5 hover:text-brand-400 transition-all">Monetization</a>

              {/* Services Mobile Section */}
              <div className="mt-2 border-b border-white/5 pb-2">
                <button
                  onClick={() => toggleMobileSection('services')}
                  className="flex items-center justify-between w-full p-4 text-lg font-bold text-slate-200 hover:text-brand-400 transition-all"
                >
                  Services
                  <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${expandedMobileSection === 'services' ? 'rotate-180 text-brand-400' : 'text-slate-500'}`} />
                </button>

                <AnimatePresence>
                  {expandedMobileSection === 'services' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden bg-white/[0.02] rounded-2xl mx-2"
                    >
                      <div className="p-4 flex flex-col gap-6">
                        <div>
                          <h4 className="text-[10px] font-black text-brand-400/60 mb-3 uppercase tracking-widest pl-2">Core Development</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {servicesMenu.core.map(s => (
                              <Link key={s.name} to={s.path} className="flex items-center gap-3 p-3 text-sm font-semibold text-slate-300 hover:bg-white/5 rounded-xl">
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">{s.icon}</div>
                                {s.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-[10px] font-black text-brand-400/60 mb-3 uppercase tracking-widest pl-2">Design & Marketing</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {servicesMenu.design.map(s => (
                              <Link key={s.name} to={s.path} className="flex items-center gap-3 p-3 text-sm font-semibold text-slate-300 hover:bg-white/5 rounded-xl">
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">{s.icon}</div>
                                {s.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Industries Mobile Section */}
              <div className="mt-2 border-b border-white/5 pb-2">
                <button
                  onClick={() => toggleMobileSection('industries')}
                  className="flex items-center justify-between w-full p-4 text-lg font-bold text-slate-200 hover:text-brand-400 transition-all"
                >
                  Industries
                  <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${expandedMobileSection === 'industries' ? 'rotate-180 text-brand-400' : 'text-slate-500'}`} />
                </button>

                <AnimatePresence>
                  {expandedMobileSection === 'industries' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden bg-white/[0.02] rounded-2xl mx-2"
                    >
                      <div className="p-4 grid grid-cols-2 gap-2">
                        {industriesMenu.slice(0, 8).map(ind => (
                          <Link key={ind.name} to={ind.path} className="flex flex-col gap-2 p-3 rounded-xl bg-white/5 border border-white/5 hover:border-brand-500/30 transition-all">
                            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">{ind.icon}</div>
                            <span className="text-[11px] font-bold text-slate-300">{ind.name}</span>
                          </Link>
                        ))}
                        <Link to="/industries" className="col-span-2 text-center p-3 text-xs font-bold text-brand-400 hover:bg-white/5 rounded-xl mt-2">View All Industries</Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Tools Mobile Section */}
              <div className="mt-2 border-b border-white/5 pb-2">
                <button
                  onClick={() => toggleMobileSection('tools')}
                  className="flex items-center justify-between w-full p-4 text-lg font-bold text-slate-200 hover:text-brand-400 transition-all"
                >
                  Our Tools
                  <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${expandedMobileSection === 'tools' ? 'rotate-180 text-brand-400' : 'text-slate-500'}`} />
                </button>

                <AnimatePresence>
                  {expandedMobileSection === 'tools' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden bg-white/[0.02] rounded-2xl mx-2"
                    >
                      <div className="p-4 flex flex-col gap-6">
                        {/* Developer Tools */}
                        <div>
                          <h4 className="text-[10px] font-black text-brand-400/60 mb-3 uppercase tracking-widest pl-2">Developer Tools</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {toolsMenu.developer.map(t => (
                              <Link key={t.name} to={t.path} className="flex items-center gap-3 p-3 text-sm font-semibold text-slate-300 hover:bg-white/5 rounded-xl">
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">{t.icon}</div>
                                {t.name}
                              </Link>
                            ))}
                          </div>
                        </div>

                        {/* Image Tools */}
                        <div>
                          <h4 className="text-[10px] font-black text-emerald-400/60 mb-3 uppercase tracking-widest pl-2">Image Tools</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {toolsMenu.image.map(t => (
                              <Link key={t.name} to={t.path} className="flex items-center gap-3 p-3 text-sm font-semibold text-slate-300 hover:bg-white/5 rounded-xl">
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">{t.icon}</div>
                                {t.name}
                              </Link>
                            ))}
                          </div>
                        </div>

                        {/* Utility Tools */}
                        <div>
                          <h4 className="text-[10px] font-black text-blue-400/60 mb-3 uppercase tracking-widest pl-2">Utility Tools</h4>
                          <div className="grid grid-cols-1 gap-1">
                            {toolsMenu.utility.filter(t => t.name !== 'Typing Test').map(t => (
                              <Link key={t.name} to={t.path} className="flex items-center gap-3 p-3 text-sm font-semibold text-slate-300 hover:bg-white/5 rounded-xl">
                                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">{t.icon}</div>
                                {t.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link to="/portfolio" className={`text-lg font-bold p-4 rounded-2xl transition-all ${location.pathname.startsWith('/portfolio') ? 'text-brand-400 bg-white/5' : 'text-slate-200 hover:bg-white/5 hover:text-brand-400'}`}>Portfolio</Link>
              <Link to="/blog" className={`text-lg font-bold p-4 rounded-2xl transition-all ${location.pathname.startsWith('/blog') ? 'text-brand-400 bg-white/5' : 'text-slate-200 hover:bg-white/5 hover:text-brand-400'}`}>Blog</Link>
              <Link to="/about-us" className={`text-lg font-bold p-4 rounded-2xl transition-all ${location.pathname.startsWith('/about-us') ? 'text-brand-400 bg-white/5' : 'text-slate-200 hover:bg-white/5 hover:text-brand-400'}`}>About</Link>

              <Link
                to="/contact-us"
                className="bg-white text-black px-6 py-5 rounded-full text-center font-black mt-6 shadow-[0_10px_30px_rgba(255,255,255,0.15)] flex items-center justify-center gap-2 text-lg transition-colors active:bg-brand-400"
              >
                Get a Quote
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
